/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useRef } from "react";

import {
  ClickAwayListener,
  Fade,
  Grow,
  Paper,
  Popper,
  Typography
} from "@mui/material";

import { ExpandMoreIcon } from "@dotnet/telematics-design-system";

import GridNavigationArrows from "../shared/components/GridNavigationArrows";
import PickerInputValue from "../shared/components/PickerInputValue";
import {
  expandMoreStyle,
  getSlideStyle,
  header,
  headerCSS,
  input,
  picker,
  rootCSS,
  slideAnimations
} from "../shared/styles/useDatePickerStyles";
import { VIEW_MODE } from "../shared/types";
import YearView from "../shared/YearView/YearView";
import MonthView from "./components/MonthView";
import { useMonthRangePicker } from "./hooks/useMonthRangePicker";
import { MonthRangePickerProps } from "./types";

const MonthRangePicker = ({
  minDate,
  maxDate,
  disabled,
  externalValue,
  customStyle,
  shouldDisableMonth,
  onChange
}: MonthRangePickerProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);

  const {
    months,
    year,
    open,
    viewMode,
    viewVisible,
    slideClass,
    startMonth,
    endMonth,
    hoverMonth,
    picking,
    noStartMonth,
    hasClearableValue,
    setHoverMonth,
    openPicker,
    closePicker,
    handleMonthClick,
    handleNavigateBack,
    handleNavigateForward,
    handleHeaderClick,
    handleYearSelect,
    handleClear,
    cancelHoverClear,
    getDisplayValue
  } = useMonthRangePicker({ disabled, externalValue, onChange });

  return (
    <div css={css(rootCSS(disabled), customStyle)}>
      <div onClick={openPicker} ref={anchorRef} css={input(open, disabled)}>
        <PickerInputValue
          displayValue={getDisplayValue()}
          isPlaceholder={noStartMonth}
          disabled={disabled}
          hasClearableValue={hasClearableValue && !disabled}
          onClear={handleClear}
        />
      </div>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: "top left" }}
            timeout={{ enter: 250, exit: 250 }}
          >
            <Paper elevation={0}>
              <ClickAwayListener onClickAway={closePicker}>
                <div css={css(picker, { padding: "16px 8px" })}>
                  <style>{slideAnimations}</style>

                  <div css={header}>
                    <Typography
                      variant="body1Medium"
                      onClick={handleHeaderClick}
                      css={css(headerCSS)}
                    >
                      <span>{year}</span>
                      <ExpandMoreIcon
                        style={expandMoreStyle(viewMode === VIEW_MODE.Year)}
                      />
                    </Typography>

                    {viewMode === VIEW_MODE.Month && (
                      <GridNavigationArrows
                        onBack={handleNavigateBack}
                        onForward={handleNavigateForward}
                        backDisabled={minDate ? year <= minDate.year() : false}
                        forwardDisabled={
                          maxDate ? year >= maxDate.year() : false
                        }
                      />
                    )}
                  </div>

                  <Fade in={viewVisible} timeout={{ enter: 160, exit: 160 }}>
                    <div
                      style={
                        viewMode === VIEW_MODE.Year
                          ? undefined
                          : { overflow: "hidden" }
                      }
                    >
                      <div css={css({ ...getSlideStyle(slideClass) })}>
                        {viewMode === VIEW_MODE.Month && (
                          <MonthView
                            year={year}
                            months={months}
                            endMonth={endMonth}
                            startMonth={startMonth}
                            hoverMonth={hoverMonth}
                            picking={picking}
                            maxDate={maxDate}
                            minDate={minDate}
                            shouldDisableMonth={shouldDisableMonth}
                            onMonthClick={handleMonthClick}
                            cancelHoverClear={cancelHoverClear}
                            setHoverMonth={setHoverMonth}
                          />
                        )}

                        {viewMode === VIEW_MODE.Year && (
                          <YearView
                            year={year}
                            currentYear={new Date().getFullYear()}
                            minDate={minDate}
                            maxDate={maxDate}
                            onYearSelect={handleYearSelect}
                          />
                        )}
                      </div>
                    </div>
                  </Fade>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default MonthRangePicker;
